import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Navigation from './Navigation';
import Breadcrumb from './Breadcrumb';
import Footer from './Footer';
import ScrollToTop from './ScrollToTop';
import { MobileOptimizer, ViewportManager } from '../utils/mobileOptimizer';

const Layout = ({ children, breadcrumbPath = null }) => {
  const location = useLocation();
  const [mobileState, setMobileState] = useState(() => {
    // Safety check for test environment
    if (typeof window === 'undefined' || !MobileOptimizer?.getOptimizationState) {
      return {
        device: { type: 'desktop', isMobile: false, isTablet: false, isDesktop: true, isTouchDevice: false, orientation: 'landscape' },
        viewport: { width: 1024, height: 768 },
        breakpoints: { current: 'desktop', isMobile: false, isTablet: false, isDesktop: true, isLargeDesktop: false, width: 1024, height: 768 },
        touchOptimizations: {}
      };
    }
    return MobileOptimizer.getOptimizationState();
  });

  // Initialize mobile optimizations on mount
  useEffect(() => {
    // Safety check for test environment
    if (typeof window === 'undefined' || !MobileOptimizer?.initialize) {
      return;
    }
    
    MobileOptimizer.initialize();
    
    // Set up viewport and orientation listeners
    const cleanupResize = ViewportManager?.addResizeListener ? ViewportManager.addResizeListener((breakpointInfo) => {
      if (MobileOptimizer?.getOptimizationState) {
        setMobileState(MobileOptimizer.getOptimizationState());
      }
    }) : () => {};
    
    const cleanupOrientation = ViewportManager?.addOrientationListener ? ViewportManager.addOrientationListener((orientationInfo) => {
      setMobileState(MobileOptimizer.getOptimizationState());
      
      // Preserve scroll position during orientation change
      const scrollPosition = window.scrollY;
      setTimeout(() => {
        window.scrollTo(0, scrollPosition);
      }, 100);
    }) : () => {};
    
    return () => {
      cleanupResize();
      cleanupOrientation();
    };
  }, []);

  // Scroll to top on route change with mobile-aware behavior
  useEffect(() => {
    // Mock-safe scroll behavior for tests
    if (typeof window !== 'undefined' && window.scrollTo) {
      try {
        // Smooth scroll on mobile, instant on desktop
        const scrollBehavior = mobileState.device.isMobile ? 'smooth' : 'auto';
        window.scrollTo({ top: 0, behavior: scrollBehavior });
      } catch (error) {
        // Fallback for test environments
        window.scrollTo(0, 0);
      }
    }
  }, [location.pathname, mobileState.device.isMobile]);

  return (
    <div className={`App ${mobileState.device.type} ${mobileState.device.orientation} ${mobileState.device.isTouchDevice ? 'touch-device' : 'no-touch'}`}>
      <Navigation mobileState={mobileState} />
      <Breadcrumb 
        customPath={breadcrumbPath || window.customBreadcrumbPath || null} 
        mobileState={mobileState} 
      />
      <main className="main-content" data-device-type={mobileState.device.type}>
        {children}
      </main>
      <Footer mobileState={mobileState} />
      <ScrollToTop mobileState={mobileState} />
    </div>
  );
};

export default Layout;
